/**
 * Authentication Store
 *
 * Manages user authentication state with backend JWT tokens.
 * No Firebase SDK on frontend - all auth handled by backend.
 */

import { defineStore } from 'pinia';
import {
    login as apiLogin,
    getCurrentUser,
    refreshUser,
} from '@/api/auth.api';
import type { AuthUser, LoginCredentials } from '@/types/auth.types';
import { AppType, UserRole, canAccessApp, hasMinRole } from '@/types/auth.types';

// =============================================================================
// Store Types
// =============================================================================

interface AuthState {
    user: AuthUser | null;
    token: string | null;
    isLoading: boolean;
    isInitialized: boolean;
    error: string | null;
}

// =============================================================================
// Auth Store
// =============================================================================

export const useAuthStore = defineStore('auth', {
    state: (): AuthState => ({
        user: null,
        token: null,
        isLoading: false,
        isInitialized: false,
        error: null,
    }),

    getters: {
        /**
         * Is user authenticated?
         */
        isAuthenticated: (state): boolean => !!state.user && !!state.token,

        /**
         * Is user a super admin?
         */
        isSuperAdmin: (state): boolean => state.user?.role === UserRole.SUPER_ADMIN,

        /**
         * Is user at least a manager?
         */
        isManager: (state): boolean =>
            state.user?.role === UserRole.SUPER_ADMIN ||
            state.user?.role === UserRole.MANAGER,

        /**
         * Can user access Fidelity app?
         */
        canAccessFidelity: (state): boolean => canAccessApp(state.user, AppType.FIDELITY),

        /**
         * Can user access SAV app?
         */
        canAccessSAV: (state): boolean => canAccessApp(state.user, AppType.SAV),

        /**
         * Get user's display name
         */
        displayName: (state): string =>
            state.user?.displayName || state.user?.email || 'User',

        /**
         * Get user's initials for avatar
         */
        initials: (state): string => {
            const name = state.user?.displayName || state.user?.email || 'U';
            return name
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
        },
    },

    actions: {
        /**
         * Initialize auth state from localStorage
         */
        async initialize(): Promise<void> {
            if (this.isInitialized) return;

            this.isLoading = true;

            try {
                // Try to restore from localStorage
                const storedToken = localStorage.getItem('authToken');
                const storedUser = localStorage.getItem('authUser');

                if (storedToken && storedUser) {
                    this.token = storedToken;
                    this.user = JSON.parse(storedUser);

                    // Verify token is still valid by calling /auth/me
                    const result = await getCurrentUser(storedToken);
                    if (result.success && result.data) {
                        this.user = result.data.user;
                        localStorage.setItem('authUser', JSON.stringify(this.user));
                    } else {
                        // Token invalid or expired, clear auth
                        this.clearAuth();
                    }
                }
            } catch (error) {
                console.error('Auth initialization failed:', error);
                this.clearAuth();
            } finally {
                this.isLoading = false;
                this.isInitialized = true;
            }
        },

        /**
         * Login with email and password
         */
        async login(credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> {
            this.isLoading = true;
            this.error = null;

            try {
                // Send credentials to backend
                const result = await apiLogin(credentials);

                if (!result.success || !result.data) {
                    throw new Error(result.error?.message || 'Login failed');
                }

                // Store auth data
                this.token = result.data.token;
                this.user = result.data.user;

                localStorage.setItem('authToken', this.token);
                localStorage.setItem('authUser', JSON.stringify(this.user));

                return { success: true };
            } catch (error: any) {
                console.error('Login failed:', error);
                const message = error.message || 'Login failed';
                this.error = message;
                return { success: false, error: message };
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Logout
         */
        async logout(): Promise<void> {
            this.clearAuth();
        },

        /**
         * Clear all auth data
         */
        clearAuth(): void {
            this.user = null;
            this.token = null;
            this.error = null;
            localStorage.removeItem('authToken');
            localStorage.removeItem('authUser');
        },

        /**
         * Refresh user data from backend
         */
        async refreshUserData(): Promise<void> {
            if (!this.token) return;

            try {
                const result = await refreshUser(this.token);
                if (result.success && result.data) {
                    this.user = result.data.user;
                    localStorage.setItem('authUser', JSON.stringify(this.user));
                    // Backend rotates the JWT on refresh so new claims take effect
                    if (result.data.token) {
                        this.token = result.data.token;
                        localStorage.setItem('authToken', this.token);
                    }
                }
            } catch (error) {
                console.error('Failed to refresh user:', error);
            }
        },

        /**
         * Check if user has at least a minimum role
         */
        hasMinRole(minRole: UserRole): boolean {
            return hasMinRole(this.user, minRole);
        },

        /**
         * Check if user can access an app
         */
        canAccessApp(app: AppType): boolean {
            return canAccessApp(this.user, app);
        },
    },
});

// =============================================================================
// Composables for easier usage in components
// =============================================================================

/**
 * Composable to check minimum role in templates
 */
export function useMinRole(minRole: UserRole): boolean {
    const authStore = useAuthStore();
    return authStore.hasMinRole(minRole);
}

/**
 * Composable to check app access in templates
 */
export function useAppAccess(app: AppType): boolean {
    const authStore = useAuthStore();
    return authStore.canAccessApp(app);
}
